import { TemaTokens } from "../interfaces/PaineisTvTema";

/** Funções puras do tema por cliente (sem DOM): o provider do portal e a TV usam as mesmas. */

const HEX_6 = /^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i;
const HEX_3 = /^#([0-9a-f])([0-9a-f])([0-9a-f])$/i;

/** "#RRGGBB" (ou "#RGB") -> [r, g, b]; `null` se o formato for inválido. */
function parseHex(hex: string): [number, number, number] | null {
  const v = hex.trim();
  const m6 = HEX_6.exec(v);
  if (m6) return [parseInt(m6[1], 16), parseInt(m6[2], 16), parseInt(m6[3], 16)];
  const m3 = HEX_3.exec(v);
  if (m3) return [parseInt(m3[1] + m3[1], 16), parseInt(m3[2] + m3[2], 16), parseInt(m3[3] + m3[3], 16)];
  return null;
}

/** Formato de canal do Tailwind (`rgb(var(--x) / <alpha-value>)`): "r g b". */
export function hexParaRgb(hex: string): string | null {
  const c = parseHex(hex);
  return c ? c.join(" ") : null;
}

/** Escurece em `percentual` (0–100) multiplicando cada canal; devolve "r g b". */
export function escurecerRgb(hex: string, percentual: number): string | null {
  const c = parseHex(hex);
  if (!c) return null;
  const fator = 1 - Math.min(Math.max(percentual, 0), 100) / 100;
  return c.map((v) => Math.round(v * fator)).join(" ");
}

export const FONTE_PADRAO_FALLBACK =
  '"Geist", "Geist Variable", "Plus Jakarta Sans", "DM Sans", system-ui, sans-serif';

const FONTES_DO_SISTEMA = new Set([
  "arial", "helvetica", "verdana", "tahoma", "trebuchet ms", "times new roman", "georgia", "courier new",
  "system-ui", "sans-serif", "serif", "monospace", "segoe ui", "geist", "geist variable",
]);

export function ehFonteDoSistema(fonte: string): boolean {
  return FONTES_DO_SISTEMA.has(fonte.trim().toLowerCase());
}

/** URL do CSS do Google Fonts para a família; `null` quando não precisa carregar nada. */
export function urlGoogleFonts(fonte: string | null): string | null {
  const nome = fonte?.trim();
  if (!nome || ehFonteDoSistema(nome)) return null;
  return `https://fonts.googleapis.com/css2?family=${encodeURIComponent(nome).replace(/%20/g, "+")}:wght@400;500;600;700&display=swap`;
}

/**
 * Variáveis CSS a aplicar em `:root`. Fundo/superfície/texto/borda só valem no tema claro
 * (o dark tem paleta própria); primária, gradiente, raio e fonte valem nos dois.
 * Valor inválido é ignorado: nunca produz variável quebrada.
 */
export function montarVariaveis(tokens: TemaTokens, escuro: boolean): Record<string, string> {
  const vars: Record<string, string> = {};
  const primaria = hexParaRgb(tokens.primaria);
  const primaria600 = escurecerRgb(tokens.primaria, 12);
  if (primaria && primaria600) {
    vars["--tema-primaria-rgb"] = primaria;
    vars["--tema-primaria-600-rgb"] = primaria600;
    const secundaria = tokens.secundaria && parseHex(tokens.secundaria) ? tokens.secundaria : tokens.primaria;
    vars["--tema-gradiente"] = `linear-gradient(122deg, ${tokens.primaria}, ${secundaria})`;
  }
  if (!escuro) {
    const claros: Array<[string, string | null]> = [
      ["--dv-canvas", tokens.fundo],
      ["--dv-surface", tokens.superficie],
      ["--dv-text", tokens.texto],
      ["--dv-text-muted", tokens.textoSuave],
      ["--dv-border", tokens.borda],
    ];
    claros.forEach(([nome, cor]) => {
      if (cor && parseHex(cor)) vars[nome] = cor;
    });
  }
  if (tokens.raio !== null && tokens.raio !== undefined) vars["--dv-radius"] = `${tokens.raio}px`;
  const fonte = tokens.fonte?.trim();
  if (fonte) vars["--dv-font-sans"] = `"${fonte}", ${FONTE_PADRAO_FALLBACK}`;
  return vars;
}

/** Nomes de todas as variáveis que o tema pode escrever (para limpar ao trocar claro/escuro). */
export const VARIAVEIS_DO_TEMA = [
  "--tema-primaria-rgb",
  "--tema-primaria-600-rgb",
  "--tema-gradiente",
  "--dv-canvas",
  "--dv-surface",
  "--dv-text",
  "--dv-text-muted",
  "--dv-border",
  "--dv-radius",
  "--dv-font-sans",
];
